import { ApiMutationOptions } from '../../hooks/useApiMutation';
import { clanFormInput, clanFormSchema } from '~/util/forms/clan';
import { Clan, ClanInvite, ClanMember } from '~/util/types/clans';
import { includeDifferentProperties } from '~/util';
import { BaseUser } from '~/util/types/users';

export const newClanMutation = (): ApiMutationOptions<Clan, clanFormInput> => ({
    buildRequest: (values) => ({
        path: `/clans`,
        method: 'POST',
        attachAuth: true,
        body: clanFormSchema.parse(values),
    }),
    invalidateQueries: ['users', '@me'],
    toastMessages: {
        loading: 'Creating clan...',
        error: 'Failed to create clan: ',
        success: 'Clan created successfully',
    },
});

export type editClanMutationType = {
    clan: clanFormInput;
    existingClan: clanFormInput;
    existingClanId: string;
};

export const editClanMutation = (): ApiMutationOptions<
    Clan,
    editClanMutationType
> => ({
    buildRequest: ({ clan, existingClan, existingClanId }) => {
        const body = includeDifferentProperties(
            clanFormSchema.parse(clan),
            existingClan,
        );
        if (Object.keys(body).length === 0)
            throw new Error('No changes have been made.');
        return {
            path: '/clans/' + existingClanId,
            method: 'PATCH',
            attachAuth: true,
            body,
        };
    },
    invalidateQueries: ['users', '@me'],
    toastMessages: {
        loading: 'Updating clan...',
        error: 'Failed to update clan: ',
        success: 'Clan updated successfully',
    },
});

export const acceptClanInviteMutation = (): ApiMutationOptions<
    void,
    ClanInvite
> => ({
    buildRequest: ({ id }) => ({
        path: `/users/@me/clan/invites/${id}/accept`,
        method: 'POST',
        attachAuth: true,
    }),
    invalidateQueries: [
        ['clan', 'invites'],
        ['users', '@me'],
    ],
    toastMessages: {
        loading: 'Accepting invite...',
        error: 'Failed to accept invite: ',
        success: ({
            variables: {
                clan: { global_name },
            },
        }) => `Invite accepted successfully to join ${global_name}`,
    },
});

export const rejectClanInviteMutation = (): ApiMutationOptions<
    void,
    ClanInvite
> => ({
    buildRequest: ({ id }) => ({
        path: `/users/@me/clan/invites/${id}/reject`,
        method: 'POST',
        attachAuth: true,
    }),
    invalidateQueries: ['clan', 'invites'],
    toastMessages: {
        loading: 'Rejecting invite...',
        error: 'Failed to reject invite: ',
        success: ({
            variables: {
                clan: { global_name },
            },
        }) => `Rejected invite to join ${global_name}`,
    },
});

export const leaveClanMutation = (): ApiMutationOptions<void, void> => ({
    buildRequest: () => ({
        path: `/users/@me/clan/leave`,
        method: 'POST',
        attachAuth: true,
    }),
    invalidateQueries: ['users', '@me'],
    toastMessages: {
        loading: 'Leaving clan...',
        error: 'Failed to leave clan: ',
        success: 'Left clan',
    },
});

export const deleteClanMutation = (): ApiMutationOptions<void, string> => ({
    buildRequest: (id) => ({
        path: `/clans/${id}`,
        method: 'DELETE',
        attachAuth: true,
    }),
    invalidateQueries: [['users', '@me'], ['clans']],
    toastMessages: {
        loading: 'Deleting clan...',
        error: 'Failed to delete clan: ',
        success: 'Clan deleted',
    },
});

export type changeMemberRoleMutationType = {
    clanid: string;
    member: ClanMember;
    newRole: number;
};

export const changeMemberRoleMutation = (): ApiMutationOptions<
    void,
    changeMemberRoleMutationType
> => ({
    buildRequest: ({ clanid, member, newRole }) => ({
        path: `/clans/${clanid}/members/${member.id}`,
        method: 'PATCH',
        attachAuth: true,
        body: { role: newRole },
    }),
    invalidateQueries: ({ variables: { clanid } }) => [
        'clan',
        clanid,
        'members',
    ],
    toastMessages: {
        loading: ({ newRole }) =>
            newRole === 2
                ? 'Transferring leadership...'
                : 'Changing member role...',
        error: ({ error, variables: { newRole } }) =>
            (newRole === 2
                ? 'Failed to transfer leadership: '
                : 'Failed to change member role: ') +
            ('data' in error ? error.data.message : error),
        success: ({
            variables: {
                newRole,
                member: { global_name },
            },
        }) =>
            newRole === 2
                ? `Transferred leadership to ${global_name}`
                : `Changed role for ${global_name}`,
    },
});

export type inviteMemberMutationType = {
    clanid: string;
    user: BaseUser;
};

export const inviteMemberMutation = (): ApiMutationOptions<
    void,
    inviteMemberMutationType
> => ({
    buildRequest: ({ clanid, user }) => ({
        path: `/clans/${clanid}/members/invite`,
        method: 'POST',
        attachAuth: true,
        body: { user_id: user.id },
    }),
    toastMessages: {
        loading: 'Inviting member...',
        error: 'Failed to invite member: ',
        success: ({
            variables: {
                user: { global_name },
            },
        }) => `Invited ${global_name}`,
    },
});

export type kickMemberMutationType = {
    clanid: string;
    member: ClanMember;
};

export const kickMemberMutation = (): ApiMutationOptions<
    void,
    kickMemberMutationType
> => ({
    buildRequest: ({ clanid, member }) => ({
        path: `/clans/${clanid}/members`,
        method: 'DELETE',
        attachAuth: true,
        body: [member.id],
    }),
    invalidateQueries: ({ variables: { clanid } }) => [
        'clan',
        clanid,
        'members',
    ],
    toastMessages: {
        loading: 'Kicking member...',
        error: 'Failed to kick member: ',
        success: ({
            variables: {
                member: { global_name },
            },
        }) => `Kicked ${global_name}`,
    },
});
